import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike, type QueryView } from '@krp/client-runtime'

import { formatInstant } from '../../../shared/uc1b/format'
import { IDLE_RECONCILE } from './deliverySlice'
import type {
  DeliveryIntentState,
  DeliveryOperation,
  DeliveryOperations,
  DeliveryReconciliation,
  DeliveryStatus,
  DeliveryVm,
  OperationFilter,
  OperationRowVm,
  OperationTone,
  ReconcileState,
  StatusCountVm,
} from './types'

/**
 * Именованные селекторы доставки каталога. Вход — результаты запросов и
 * намерение фичи (не RootState): пакет тестируется без приложения (F03).
 */
export interface DeliveryVmInput {
  resourceId: string
  status: QueryResultLike<DeliveryStatus>
  operations: QueryResultLike<DeliveryOperations>
  reconciliation: QueryResultLike<DeliveryReconciliation>
  filter: OperationFilter
  reconcile: ReconcileState
}

export const NOT_RUN_CODE = 'reconciliation-not-run'

export const selectOperationFilter = (intent: DeliveryIntentState, resourceId: string): OperationFilter =>
  intent.filters[resourceId] ?? 'all'

export const selectReconcileState = (intent: DeliveryIntentState, resourceId: string): ReconcileState =>
  intent.reconcile[resourceId] ?? IDLE_RECONCILE

const STATUS_TEXT: Readonly<Record<string, { text: string; tone: OperationTone }>> = {
  Pending: { text: 'Ожидает отправки', tone: 'active' },
  Sent: { text: 'Отправлена, ждёт подтверждения', tone: 'active' },
  Unknown: { text: 'Исход неизвестен — идёт проверка чтением', tone: 'attention' },
  Failed: { text: 'Не доставлена', tone: 'attention' },
  Succeeded: { text: 'Доставлена', tone: 'done' },
}

const FILTER_STATUSES: Readonly<Record<OperationFilter, readonly string[] | null>> = {
  all: null,
  active: ['Pending', 'Sent'],
  unknown: ['Unknown'],
  failed: ['Failed'],
  succeeded: ['Succeeded'],
}

function rowOf(operation: DeliveryOperation): OperationRowVm {
  const known = STATUS_TEXT[operation.status]
  return {
    id: operation.operationId,
    status: operation.status,
    statusText: known?.text ?? operation.status,
    tone: known?.tone ?? 'attention',
    title: `№ ${operation.sequence} · ${operation.sourceRemoteKey}`,
    step: `Шаг: ${operation.step} (${operation.stepState}), отправок: ${operation.stepSends}`,
    error:
      operation.errorMessage !== null || operation.errorCode !== null
        ? [operation.errorCode, operation.errorMessage].filter((part) => part !== null).join(': ')
        : null,
    timing: `принята ${formatInstant(operation.admittedAt)}, изменена ${formatInstant(operation.updatedAt)}`,
    nextAttempt: operation.nextAttemptAt === null ? null : `следующая попытка ${formatInstant(operation.nextAttemptAt)}`,
  }
}

function pauseText(status: DeliveryStatus | undefined): string | null {
  if (status === undefined || status.pauseReason === null) return null
  const retry = status.retryAt === null ? '' : `, повтор ${formatInstant(status.retryAt)}`
  return `Доставка приостановлена: ${status.pauseReason}${retry}`
}

const EMPTY_OPERATIONS: readonly DeliveryOperation[] = []

function viewOf<T>(result: QueryResultLike<T>, isEmpty: (data: T) => boolean): QueryView {
  return queryView(result, isEmpty)
}

export const makeSelectDeliveryVm = () => {
  const selectStatusView = createSelector([(input: DeliveryVmInput) => input.status], (status) =>
    viewOf(status, () => false),
  )
  const selectCounts = createSelector([(input: DeliveryVmInput) => input.status.data], (status): readonly StatusCountVm[] =>
    status === undefined
      ? []
      : [
          { key: 'total', label: 'Всего операций', value: status.total, attention: false },
          { key: 'pending', label: 'В очереди', value: status.pending, attention: false },
          { key: 'unknown', label: 'Исход неизвестен', value: status.unknown, attention: status.unknown > 0 },
          { key: 'failed', label: 'Не доставлено', value: status.failed, attention: status.failed > 0 },
          { key: 'succeeded', label: 'Доставлено', value: status.succeeded, attention: false },
        ],
  )
  const selectOperationsView = createSelector([(input: DeliveryVmInput) => input.operations], (operations) =>
    viewOf(operations, (data) => data.items.length === 0),
  )
  const selectRows = createSelector(
    [(input: DeliveryVmInput) => input.operations.data, (input: DeliveryVmInput) => input.filter],
    (operations, filter): readonly OperationRowVm[] => {
      const statuses = FILTER_STATUSES[filter]
      return (operations?.items ?? EMPTY_OPERATIONS)
        .filter((operation) => statuses === null || statuses.includes(operation.status))
        .map(rowOf)
    },
  )
  const selectReconciliation = createSelector(
    [
      (input: DeliveryVmInput) => input.reconciliation,
      (input: DeliveryVmInput) => input.reconcile,
    ],
    (result, reconcile): DeliveryVm['reconciliation'] => {
      const data = result.data
      const notRun = result.isError && (result.error as { code?: unknown } | undefined)?.code === NOT_RUN_CODE
      const query = notRun
        ? ({ status: 'ready', refreshing: false, error: null } satisfies QueryView)
        : viewOf(result, () => false)
      return {
        query,
        notRun,
        checkedAtText: data === undefined ? 'нет' : formatInstant(data.checkedAt),
        noSnapshot: data !== undefined && data.snapshotCompletedAt === null,
        rows:
          data === undefined
            ? []
            : [
                { key: 'bindings', label: 'Привязок', value: data.bindings, attention: false },
                { key: 'confirmed', label: 'Подтверждено чтением кабинета', value: data.confirmed, attention: false },
                { key: 'pending', label: 'Ждут подтверждения', value: data.pending, attention: false },
                { key: 'missing', label: 'Нет в кабинете', value: data.missingInCabinet, attention: data.missingInCabinet > 0 },
                { key: 'deleted', label: 'Удалены в кабинете', value: data.deletedInCabinet, attention: data.deletedInCabinet > 0 },
                { key: 'changed', label: 'Изменены в кабинете', value: data.changedInCabinet, attention: data.changedInCabinet > 0 },
                {
                  key: 'unknown',
                  label: 'Неизвестные, назначено чтение',
                  value: data.unknownScheduledForRead,
                  attention: data.unknownScheduledForRead > 0,
                },
              ],
        busy: reconcile.busy,
        failure: reconcile.failure,
      }
    },
  )

  return createSelector(
    [
      (input: DeliveryVmInput) => input.resourceId,
      (input: DeliveryVmInput) => input.status.data,
      selectStatusView,
      selectCounts,
      selectOperationsView,
      selectRows,
      (input: DeliveryVmInput) => input.operations.data,
      (input: DeliveryVmInput) => input.filter,
      selectReconciliation,
    ],
    (resourceId, status, statusView, counts, operationsView, rows, operations, filter, reconciliation): DeliveryVm => ({
      resourceId,
      status: {
        query: statusView,
        counts,
        active: status !== undefined && status.pending + status.unknown > 0,
        checkpointText: status === undefined ? 'нет' : `до операции № ${status.checkpoint} всё завершено`,
        pause: pauseText(status),
        bindingsText:
          status === undefined
            ? ''
            : `Привязок ${status.bindings}: подтверждено ${status.confirmedBindings}, ждут подтверждения ${status.pendingBindings}, расходятся с кабинетом ${status.driftedBindings}`,
      },
      operations: {
        query: operationsView,
        rows,
        truncated: operations !== undefined && !operations.complete,
        filter,
        total: operations?.items.length ?? 0,
      },
      reconciliation,
    }),
  )
}
